import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  TextField,
  Button,
  Breadcrumbs,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import { isMSP } from "../../helpers/util_helper";
import {
  getBillingProfile,
  getCustomers,
  updateBillingProfile,
  clearBillingManagementMessages,
} from "../../store/billingManagement/billingManagementSlice";

const defaultProfile = () => ({
  name: "Default",
  description: "",
  invoice_date: 15,
});

const ConfigureBillingProfile = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const initializedRef = useRef(false);

  const isMSPUser = isMSP();
  const {
    billingProfile,
    isFetchingProfile,
    fetchProfileError,
    customers,
    isFetchingCustomers,
    isUpdatingProfile,
    updateProfileSuccess,
    updateProfileError,
  } = useSelector((state) => state.billingManagement);

  const [customersList, setCustomersList] = useState([]);
  const [mspProfile, setMspProfile] = useState(defaultProfile());
  const [mspModalOpen, setMspModalOpen] = useState(false);
  const [customerEditModalOpen, setCustomerEditModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editForm, setEditForm] = useState(defaultProfile());
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (!isMSPUser) {
      history.push("/dashboard");
      return;
    }
    dispatch(getBillingProfile());
  }, [isMSPUser, history, dispatch]);

  useEffect(() => {
    if (isFetchingProfile) return;
    if (billingProfile && (billingProfile.msp_profile || billingProfile.customers)) {
      if (billingProfile.msp_profile) setMspProfile({ ...defaultProfile(), ...billingProfile.msp_profile });
      setCustomersList(Array.isArray(billingProfile.customers) ? billingProfile.customers : []);
      initializedRef.current = true;
      return;
    }
    if (billingProfile === null && !fetchProfileError) {
      dispatch(getCustomers({ page: 1, page_size: 100 }));
    }
  }, [isFetchingProfile, billingProfile, fetchProfileError, dispatch]);

  // Refetch profile after save so table and button state stay in sync
  useEffect(() => {
    if (updateProfileSuccess) {
      dispatch(getBillingProfile());
    }
  }, [updateProfileSuccess, dispatch]);

  useEffect(() => {
    if (billingProfile !== null || !customers?.length || initializedRef.current) return;
    initializedRef.current = true;
    setMspProfile(defaultProfile());
    setCustomersList(
      customers.map((c) => ({
        org_code: c.org_code,
        org_name: c.org_name,
        profile: { name: "Default", description: "", invoice_date: 15 },
      }))
    );
  }, [billingProfile, customers]);

  useEffect(() => {
    if (updateProfileSuccess) {
      setSnackbar({ open: true, message: "Billing profile saved successfully.", severity: "success" });
      dispatch(clearBillingManagementMessages());
      dispatch(getBillingProfile());
      setMspModalOpen(false);
      setCustomerEditModalOpen(false);
      setEditingCustomer(null);
    }
  }, [updateProfileSuccess, dispatch]);

  useEffect(() => {
    if (updateProfileError) {
      const msg = typeof updateProfileError === "string" ? updateProfileError : (updateProfileError?.error || updateProfileError?.detail || "Failed to save billing profile.");
      setSnackbar({ open: true, message: msg, severity: "error" });
    }
  }, [updateProfileError]);

  const hasMspProfile = billingProfile?.msp_profile && (billingProfile.msp_profile.name != null || billingProfile.msp_profile.invoice_date != null);
  const customerActionsDisabled = billingProfile == null || billingProfile?.msp_profile == null;

  const handleOpenMspModal = () => {
    setEditForm({ ...defaultProfile(), ...mspProfile });
    setMspModalOpen(true);
  };

  const handleSaveMspProfile = () => {
    const invoiceDate = parseInt(editForm.invoice_date, 10);
    if (isNaN(invoiceDate) || invoiceDate < 1 || invoiceDate > 31) {
      setSnackbar({ open: true, message: "Invoice date must be between 1 and 31.", severity: "error" });
      return;
    }
    dispatch(updateBillingProfile({
      name: editForm.name?.trim() ?? "",
      description: editForm.description?.trim() ?? "",
      invoice_date: invoiceDate,
    }));
  };

  const handleOpenCustomerEdit = (customer) => {
    setEditingCustomer(customer);
    setEditForm({ ...defaultProfile(), ...(customer?.profile || {}) });
    setCustomerEditModalOpen(true);
  };

  const handleSaveCustomerProfile = () => {
    if (!editingCustomer) return;
    const invoiceDate = parseInt(editForm.invoice_date, 10);
    if (isNaN(invoiceDate) || invoiceDate < 1 || invoiceDate > 31) {
      setSnackbar({ open: true, message: "Invoice date must be between 1 and 31.", severity: "error" });
      return;
    }
    dispatch(updateBillingProfile({
      name: editForm.name?.trim() ?? "",
      description: editForm.description?.trim() ?? "",
      invoice_date: invoiceDate,
      customer_orgcode: editingCustomer.org_code,
    }));
  };

  const loading = isFetchingProfile || (billingProfile === null && isFetchingCustomers);

  return (
    <Box
      sx={{
        padding: { xs: "1rem", sm: "1.5rem", md: "2rem" },
        mt: "4rem",
        mb: "1.5rem",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
        fontFamily: '"Inter", system-ui, sans-serif',
      }}
    >
      {/* Row 1: Breadcrumbs left, Back right */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Breadcrumbs
          sx={{
            fontSize: "0.875rem",
            "& .MuiBreadcrumbs-separator": { color: "#94a3b8", mx: 0.5 },
          }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={() => history.push("/billing-management")}
            sx={{ color: "#64748b", textDecoration: "none", cursor: "pointer", fontSize: "0.875rem", "&:hover": { color: "#0f172a" } }}
          >
            Billing Management
          </Link>
          <Typography
            variant="body2"
            sx={{
              color: "#3b82f6",
              fontSize: "0.875rem",
              fontWeight: 600,
              px: 0.5,
            }}
          >
            Configure Billing Profiles
          </Typography>
        </Breadcrumbs>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => history.push("/billing-management")}
          sx={{ textTransform: "none", color: "#64748b", "&:hover": { bgcolor: "rgba(0,0,0,0.04)" } }}
        >
          Back
        </Button>
      </Box>

      {/* Row 2: Header left, Default billing profile button right */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: "#0f172a", fontSize: "1.25rem", mb: 0.5 }}>
            Configure Billing Profiles
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.875rem" }}>
            Customer Billing Profiles. Edit the default MSP profile or override per customer.
          </Typography>
        </Box>
        {!loading && !fetchProfileError && (
          hasMspProfile ? (
            <Button
              variant="contained"
              onClick={handleOpenMspModal}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.8125rem",
                borderRadius: "6px",
                backgroundColor: "#3b82f6",
                "&:hover": { backgroundColor: "#2563eb" },
              }}
            >
              Default Billing Profile
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleOpenMspModal}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.8125rem",
                borderRadius: "6px",
                backgroundColor: "#3b82f6",
                "&:hover": { backgroundColor: "#2563eb" },
              }}
            >
              Configure MSP Billing Profile
            </Button>
          )
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={40} sx={{ color: "#3b82f6" }} />
        </Box>
      ) : fetchProfileError && billingProfile === null ? (
        <Card variant="outlined" sx={{ p: 3 }}>
          <Typography color="error">{typeof fetchProfileError === "string" ? fetchProfileError : fetchProfileError?.error || fetchProfileError?.detail || "Failed to load billing profile."}</Typography>
        </Card>
      ) : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              <Table size="small" sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow sx={{ "& th": { backgroundColor: "#f4f4f5", borderBottom: "2px solid #e4e4e7", py: 1.5, px: 2, fontWeight: 600, fontSize: "0.875rem", color: "#52525b" } }}>
                    <TableCell>Customer</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Invoice date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customersList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: "#71717a", fontSize: "0.875rem" }}>
                        No customers found
                      </TableCell>
                    </TableRow>
                  ) : (
                    customersList.map((c) => (
                      <TableRow key={c.org_code} sx={{ "& td": { borderBottom: "1px solid rgba(0,0,0,0.06)", py: 1.5, px: 2, fontSize: "0.875rem" } }}>
                        <TableCell sx={{ fontWeight: 500, color: "#18181b" }}>{c.org_name || c.org_code}</TableCell>
                        <TableCell sx={{ color: "#52525b" }}>{c.profile?.name ?? "—"}</TableCell>
                        <TableCell sx={{ color: "#52525b" }}>{(c.profile?.description || "").trim() || "—"}</TableCell>
                        <TableCell sx={{ color: "#52525b" }}>{c.profile?.invoice_date ?? "—"}</TableCell>
                        <TableCell align="right">
                          <Tooltip title={customerActionsDisabled ? "Configure MSP Billing Profile first" : "Edit customer Billing Profile"}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={customerActionsDisabled}
                                onClick={() => !customerActionsDisabled && handleOpenCustomerEdit(c)}
                                sx={{
                                  color: customerActionsDisabled ? "#d1d5db" : "#71717a",
                                  "&:hover:not(.Mui-disabled)": { color: "#18181b", bgcolor: "rgba(0,0,0,0.04)" },
                                }}
                                aria-label="Edit"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* MSP / Default profile modal */}
      <Dialog open={mspModalOpen} onClose={() => setMspModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1.125rem" }}>{hasMspProfile ? "Edit default billing profile" : "Configure MSP billing profile"}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Name"
              value={editForm.name || ""}
              onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
              size="small"
              fullWidth
              placeholder="e.g. Standard Net15"
            />
            <TextField
              label="Description"
              value={editForm.description || ""}
              onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
              size="small"
              fullWidth
              multiline
              rows={2}
            />
            <FormControl size="small" fullWidth required>
              <InputLabel>Invoice date (day of month)</InputLabel>
              <Select
                value={editForm.invoice_date ?? 15}
                label="Invoice date (day of month)"
                onChange={(e) => setEditForm((p) => ({ ...p, invoice_date: Number(e.target.value) }))}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <MenuItem key={day} value={day}>
                    Day {day}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e5e7eb" }}>
          <Button onClick={() => setMspModalOpen(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveMspProfile} disabled={isUpdatingProfile} sx={{ textTransform: "none" }}>
            {isUpdatingProfile ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Customer profile edit modal */}
      <Dialog open={customerEditModalOpen} onClose={() => { setCustomerEditModalOpen(false); setEditingCustomer(null); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1.125rem" }}>
          Edit Billing Profile {editingCustomer?.org_name ? `— ${editingCustomer.org_name}` : ""}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Name"
              value={editForm.name || ""}
              onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
              size="small"
              fullWidth
            />
            <TextField
              label="Description"
              value={editForm.description || ""}
              onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
              size="small"
              fullWidth
              multiline
              rows={2}
            />
            <FormControl size="small" fullWidth required>
              <InputLabel>Invoice date (day of month)</InputLabel>
              <Select
                value={editForm.invoice_date ?? 15}
                label="Invoice date (day of month)"
                onChange={(e) => setEditForm((p) => ({ ...p, invoice_date: Number(e.target.value) }))}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <MenuItem key={day} value={day}>
                    Day {day}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e5e7eb" }}>
          <Button onClick={() => { setCustomerEditModalOpen(false); setEditingCustomer(null); }} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveCustomerProfile} disabled={isUpdatingProfile} sx={{ textTransform: "none" }}>
            {isUpdatingProfile ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConfigureBillingProfile;
